import type { CreatePrefill } from '@/components/vyroba/TabidooDetailModal';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

type QueueItem = {
  id: number;
  term_date: string;
  product_code: string;
  product_name: string;
  /** Tabidoo id produktu — modal nové zakázky pracuje s propojením, ne s kódem. */
  product_id: string;
  image_url: string | null;
  sizes: { variant_name: string | null; qty: number }[];
  total_qty: number;
  created_at: number;
  created_by: string;
};

/** 'YYYY-MM-DD' → '4.9.'. */
function fmtShort(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number.parseInt(d, 10)}.${Number.parseInt(m, 10)}.` : iso;
}

/**
 * Upozornění nad tabulkou Výroby: co střihárna nastříhala a čeká to na
 * založení zakázky. „Zaevidovat" otevře modal nové zakázky s předvyplněným
 * produktem a kusy; položka se z fronty odebere až po uložení.
 */
export function ZaevidovaneFronta({
  onCreate,
  reload,
}: {
  onCreate: (prefill: CreatePrefill, onSaved: () => void) => void;
  reload: () => Promise<void>;
}) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ items: QueueItem[] }>('/api/vyroba/zaevidovano');
      setItems(d.items);
    } catch {
      /* fronta je doplňková informace, kvůli ní stránku neshazujeme */
    }
  }, []);

  // Střihárna eviduje na tabletu, ale zakázku zakládá kolegyně u PC — bez
  // vlastního dotazování by musela stránku pořád obnovovat. Ptáme se každých
  // 15 vteřin a hned, jakmile se okno vrátí do popředí; na skryté kartě ne,
  // ať zbytečně netlučeme do serveru.
  useEffect(() => {
    load();
    const jePoTom = () => document.visibilityState === 'visible';
    const timer = window.setInterval(() => {
      if (jePoTom()) load();
    }, 15000);
    const probuzeni = () => {
      if (jePoTom()) load();
    };
    document.addEventListener('visibilitychange', probuzeni);
    window.addEventListener('focus', probuzeni);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', probuzeni);
      window.removeEventListener('focus', probuzeni);
    };
  }, [load]);

  /** Otevře zakládání zakázky s produktem a kusy z fronty. */
  function zaevidovat(item: QueueItem) {
    if (!item.product_id) {
      setErr(`Produkt ${item.product_code} není v Databázi produktů — zakázku založ ručně.`);
      return;
    }
    const sizes: Record<string, number> = {};
    for (const s of item.sizes) if (s.variant_name) sizes[s.variant_name] = s.qty;
    setOpen(false);
    onCreate({ fields: { kodProduktu: item.product_id }, sizes }, async () => {
      // Uloženo → položka z fronty ven a seznam zakázek přenačíst.
      await apiFetch(`/api/vyroba/zaevidovano/${item.id}/vyresit`, { method: 'POST', body: {} });
      await Promise.all([load(), reload()]);
    });
  }

  async function zahodit(item: QueueItem) {
    try {
      await apiFetch(`/api/vyroba/zaevidovano/${item.id}/vyresit`, { method: 'POST', body: {} });
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Odebrání z fronty selhalo');
    }
  }

  if (items.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-4 flex w-full items-center gap-3 rounded-lg border border-warning/40 bg-warning-light px-4 py-3 text-left hover:opacity-90 cursor-pointer transition-opacity"
      >
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-warning text-white text-sm font-bold">
          {items.length}
        </span>
        <span className="text-sm font-bold text-text">Rozpracované zakázky k dokončení</span>
        <span className="text-sm text-text-secondary">
          Střihárna nastříhala {items.length === 1 ? 'produkt' : 'produkty'}, čekají na založení
          zakázky.
        </span>
        <span className="ml-auto text-sm font-semibold text-text-secondary">Otevřít →</span>
      </button>

      {open && (
        <Modal
          items={items}
          err={err}
          onZaevidovat={zaevidovat}
          onZahodit={zahodit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function Modal({
  items,
  err,
  onZaevidovat,
  onZahodit,
  onClose,
}: {
  items: QueueItem[];
  err: string | null;
  onZaevidovat: (item: QueueItem) => void;
  onZahodit: (item: QueueItem) => void;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/60 cursor-default"
      />
      <div className="relative w-full max-w-3xl my-6 bg-surface rounded-2xl shadow-modal reveal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-bold text-text">Rozpracované zakázky k dokončení</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-text text-xl leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5">
          {err && (
            <div className="mb-4 rounded-lg border border-danger/30 bg-danger-light px-4 py-3 text-sm text-danger">
              {err}
            </div>
          )}
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 rounded-lg border border-border px-4 py-3 flex-wrap"
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt=""
                    loading="lazy"
                    className="h-14 w-14 shrink-0 rounded-md border border-border bg-white object-contain p-1"
                  />
                ) : (
                  <div className="h-14 w-14 shrink-0 rounded-md border border-border bg-bg" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-text-muted">{item.product_code}</span>
                    <span className="text-sm font-semibold text-text">{item.product_name}</span>
                    <span className="text-xs text-text-muted">
                      termín {fmtShort(item.term_date)}
                    </span>
                  </div>
                  <div className="mt-1 text-[13px] text-text-secondary">
                    {item.sizes
                      .map((s) => `${s.variant_name ?? 'bez vel.'} × ${s.qty}`)
                      .join(' · ')}
                    <strong className="ml-2 font-semibold text-text">
                      celkem {item.total_qty} ks
                    </strong>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onZahodit(item)}
                  title="Odebrat z fronty bez založení zakázky"
                  className="text-sm font-semibold text-text-secondary hover:text-danger px-2 py-2 cursor-pointer"
                >
                  Odebrat
                </button>
                <button
                  type="button"
                  onClick={() => onZaevidovat(item)}
                  className="bg-action hover:bg-action-dark text-white text-sm font-bold rounded-lg px-5 py-2.5 cursor-pointer transition-colors"
                >
                  Zaevidovat
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
